import jwtDecode from 'jwt-decode';
import { ACCESS_TOKEN, AUTH_ACCESS_TOKEN, PIPELINE_ID_SESSION_KEY, RULESET_ID_SESSION_KEY } from 'Utils';
import { create } from 'zustand';
import { BrighterionAuthService } from '@brighterion/br-auth-service';
import { RuleManagementRoleService } from '@brighterion/br-roles';
import { setAuthService, setRoleService, setUserRuleSetId } from 'Services';
import type { AuthConfig } from '@brighterion/types/config';
import { initServices } from '../../services/initServices';
import { getConfig } from '../../config';
import { getAccessToken } from './utils';

type AuthenticationState = {
    authConfig?: AuthConfig;
    authService?: BrighterionAuthService;
    fetchUser: () => Promise<void>;
    impersonateTenant: (tenantKey: string) => Promise<void>;
    isAuthenticated: boolean;
    logout: () => Promise<void>;
    roles: string[];
    stopImpersonation: (landlordId: string) => Promise<void>;
    switchRuleSet: () => Promise<void>;
    userInfo?: UserInfo;
};

export const useAuthenticationStore = create<AuthenticationState>((set, get) => ({
    authConfig: undefined,
    authService: undefined,
    fetchUser: async () => {
        set({ isAuthenticated: false });
        /**
         * If user refresh the browser while impersonating the Tenant and
         * the access token is invalid. The App will redirect the user to Okta,
         * and Okta returns the access token from before impersonation.
         * To aviod any config conflicts remove items from the session Storage.
         */
        if (!sessionStorage.getItem(AUTH_ACCESS_TOKEN) && !sessionStorage.getItem(ACCESS_TOKEN)) {
            sessionStorage.removeItem(RULESET_ID_SESSION_KEY);
            sessionStorage.removeItem(PIPELINE_ID_SESSION_KEY);
        }

        const config = await getConfig();
        const { auth, ...appConfig } = config;
        const authService = new BrighterionAuthService(auth.type, auth);
        await authService.init();

        const accessToken = await getAccessToken({ authService });
        const userInfo: UserInfo = jwtDecode<JWT>(accessToken);

        const roles = userInfo.roles.split(',');
        const roleService = new RuleManagementRoleService(roles);

        /** Deprecated - RoleService and AuthService needs to be replaced by Zustand Store. */
        setRoleService(roleService);
        setAuthService(authService);
        await initServices(appConfig);

        /** Important - The Rule Set ID is crucial for the App */
        await setUserRuleSetId();

        set({
            authConfig: auth,
            authService,
            isAuthenticated: true,
            roles,
            userInfo
        });
    },
    impersonateTenant: async (tenantKey) => {
        set({ isAuthenticated: false });

        const auth = get().authConfig!;
        const authService = new BrighterionAuthService(auth.type, auth);
        await authService.init(tenantKey);

        const accessToken = await getAccessToken({ authService });
        const userInfo: UserInfo = jwtDecode<JWT>(accessToken);

        const roles = userInfo.roles.split(',');
        const roleService = new RuleManagementRoleService(roles);

        /** Deprecated - RoleService and AuthService needs to be replaced by Zustand Store. */
        setRoleService(roleService);
        setAuthService(authService);

        /** Important - The Rule Set ID is crucial for the App */
        await setUserRuleSetId();

        set({
            authService,
            isAuthenticated: true,
            roles,
            userInfo
        });
    },
    isAuthenticated: false,
    logout: async () => {
        const auth = get().authService;
        if (auth) {
            await auth.logout();
            set({ isAuthenticated: false });
            sessionStorage.clear();
            localStorage.clear();
            window.location.replace('/');
        } else {
            throw new Error('Logout failed: BrighterionAuthService is `undefined`');
        }
    },
    roles: [],
    stopImpersonation: async (landlordId) => {
        set({ isAuthenticated: false });

        const auth = get().authConfig!;
        const authService = new BrighterionAuthService(auth.type, auth);
        await authService.init(landlordId);

        const accessToken = await getAccessToken({ authService });
        const userInfo: UserInfo = jwtDecode<JWT>(accessToken);

        const roles = userInfo.roles.split(',');
        const roleService = new RuleManagementRoleService(roles);

        /** Deprecated - RoleService and AuthService needs to be replaced by Zustand Store. */
        setRoleService(roleService);
        setAuthService(authService);

        /** Important - The Rule Set ID is crucial for the App */
        await setUserRuleSetId();

        set({
            authService,
            isAuthenticated: true,
            roles,
            userInfo
        });
    },
    switchRuleSet: async () => {
        set({ isAuthenticated: false });

        const auth = get().authConfig!;
        const authService = new BrighterionAuthService(auth.type, auth);
        await authService.init();

        const userInfo: UserInfo = await authService.getUserInfo();

        const roles = userInfo.roles.split(',');
        const roleService = new RuleManagementRoleService(roles);

        /** Deprecated - RoleService and AuthService needs to be replaced by Zustand Store. */
        setRoleService(roleService);
        setAuthService(authService);

        /** Important - The Rule Set ID is crucial for the App */
        await setUserRuleSetId();

        set({
            authService,
            isAuthenticated: true,
            roles,
            userInfo
        });
    },
    userInfo: undefined
}));
